import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { Button, Card, CardBody, Col, Container, Form, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"
import moment from "moment-timezone"
import swal from "sweetalert"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

import Breadcrumbs from "components/Common/Breadcrumb"
import StickerToolbarButtons from "../../components/Common/sticker-toolbar-buttons"
import FormFilterCompany from "../../components/Common/form-filter-company"
import FormFilterSelect from "../../components/Common/form-filter-select"
import FormFilterText from "../../components/Common/form-filter-text"
import usePermission from "helpers/permission/usePermission"
import { getCompanies } from "../../helpers/utils"
import { navegarBreadCrumb } from "../../store/navigator/actions"
import { buscarPlanoContasCadastro, limparImpactosPlanoContas, salvarPlanoContasLote } from "../../store/planoContas/actions"
import PlanoContasGrade from "./plano-contas-grade"
import { montarLinhas, moverConta, naturezaDe, nivelDoCodigo, proximoCodigo } from "./plano-contas-arvore"

const NIVEL_MAXIMO = 5

const codigoPai = codigo => {
  const fim = codigo.lastIndexOf(".")
  return fim === -1 ? null : codigo.substring(0, fim)
}

const semAcento = texto => (texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

// Cópia de trabalho: cada conta guarda o original para saber o que mudou
const paraEdicao = contas => contas.map(conta => ({
  ...conta,
  chave: `id-${ conta.id }`,
  original: conta,
  novo: false,
  excluido: false,
}))

// Só os campos que o usuário mudou vão no lote: assim a gravação não desfaz, com valores velhos,
// uma alteração que outro usuário fez nos campos não tocados
const camposAlterados = conta => {
  if (conta.novo || conta.excluido) return {}
  const { original } = conta
  const campos = {}
  if (conta.codigo !== original.codigo) campos.codigo = conta.codigo
  if (conta.descricao !== original.descricao) campos.descricao = conta.descricao
  if (naturezaDe(conta.tipo) !== naturezaDe(original.tipo)) campos.tipo = naturezaDe(conta.tipo)
  if (Number(conta.dre || 0) !== Number(original.dre || 0)) campos.dre = Number(conta.dre || 0)
  if ((conta.conta_sped || "0") !== (original.conta_sped || "0")) campos.conta_sped = conta.conta_sped || "0"
  return campos
}

const foiAlterada = conta => Object.keys(camposAlterados(conta)).length > 0

const PlanoContas = props => {
  const { t } = props
  const permissoes = {
    inserir: usePermission("planoContas_inserir"),
    editar: usePermission("planoContas_editar"),
    excluir: usePermission("planoContas_excluir"),
  }

  const [empresas, setEmpresas] = useState([])
  const [empresaId, setEmpresaId] = useState()
  const [contas, setContas] = useState([])
  const [recolhidos, setRecolhidos] = useState(new Set())
  const [pesquisa, setPesquisa] = useState("")
  const [filtroNatureza, setFiltroNatureza] = useState("")
  const [focoChave, setFocoChave] = useState(null)
  const [avancado, setAvancado] = useState(null)
  const [spedAvancado, setSpedAvancado] = useState("")
  const enviados = useRef([])
  const contadorNovas = useRef(0)

  useEffect(() => {
    props.navegarBreadCrumb({ title: "menu.label.chart.of.accounts", link: props.location?.pathname })
  }, [])

  // A API só libera empresas com vínculo: oferecer outras (como faz o seletor do administrador)
  // levaria a um acesso negado
  useEffect(() => {
    if (!props.user) return
    const opcoes = getCompanies(props.user)
    setEmpresas(opcoes)
    if (opcoes.some(opcao => opcao.value === empresaId)) return
    const inicial = opcoes[0]?.value
    setEmpresaId(inicial)
    if (inicial) props.buscarPlanoContasCadastro(inicial)
  }, [props.user])

  useEffect(() => { setContas(paraEdicao(props.contas || [])) }, [props.contas])

  useEffect(() => {
    if (props.salvoEm) toastr.success(t("planocontas.salvar.sucesso"))
  }, [props.salvoEm])

  useEffect(() => {
    if (props.erro) toastr.error(t(props.erro))
  }, [props.erro])

  useEffect(() => {
    if (!focoChave) return
    document.getElementById(`descricao-${ focoChave }`)?.focus()
    setFocoChave(null)
  }, [focoChave, contas])

  const ativas = useMemo(() => contas.filter(conta => !conta.excluido), [contas])

  const pendentes = useMemo(() => ({
    novas: contas.filter(conta => conta.novo).length,
    alteradas: contas.filter(foiAlterada).length,
    excluidas: contas.filter(conta => conta.excluido).length,
  }), [contas])
  const totalPendentes = pendentes.novas + pendentes.alteradas + pendentes.excluidas

  const linhas = useMemo(() => {
    const termo = semAcento(pesquisa.trim())
    return montarLinhas(ativas, termo ? new Set() : recolhidos)
      .filter(linha => termo || linha.visivel)
      .filter(linha => !termo || semAcento(linha.codigo).includes(termo) || semAcento(linha.descricao).includes(termo))
      .filter(linha => !filtroNatureza || naturezaDe(linha.tipo) === filtroNatureza)
      .map(linha => ({
        ...linha,
        alterado: foiAlterada(linha),
        podeTerSubconta: nivelDoCodigo(linha.codigo) < NIVEL_MAXIMO && proximoCodigo(ativas, linha.codigo) !== null,
      }))
  }, [ativas, recolhidos, pesquisa, filtroNatureza])

  const contagem = useMemo(() => ({
    todas: ativas.length,
    R: ativas.filter(conta => naturezaDe(conta.tipo) === "R").length,
    D: ativas.filter(conta => naturezaDe(conta.tipo) === "D").length,
  }), [ativas])

  const errosPorChave = useMemo(() => (props.erros || []).reduce((mapa, erro) => {
    const chave = enviados.current[erro.indice]
    return chave ? { ...mapa, [chave]: t(`planocontas.erro.campo.${ erro.campo }`) } : mapa
  }, {}), [props.erros])

  // Erro em linha escondida pela pesquisa, filtro ou recolhimento não pode passar em silêncio
  useEffect(() => {
    const erros = props.erros || []
    if (erros.length === 0) return
    setPesquisa("")
    setFiltroNatureza("")
    setRecolhidos(new Set())
    const chavesComErro = new Set(erros.map(erro => enviados.current[erro.indice]))
    const emExcluida = contas.some(conta => conta.excluido && chavesComErro.has(conta.chave))
    const total = chavesComErro.size
    const resumo = total === 1 ? t("planocontas.erro.validacao.uma") : t("planocontas.erro.validacao.varias", { total })
    toastr.error(emExcluida ? `${ resumo } ${ t("planocontas.erro.excluida") }` : resumo)
  }, [props.erros])

  const alterar = (chave, campos) => setContas(atuais => atuais.map(conta => (conta.chave === chave ? { ...conta, ...campos } : conta)))

  // A natureza desce pela árvore: a conta e todas as subcontas ficam com a mesma
  const alterarNatureza = (linha, tipo) => setContas(atuais => atuais.map(conta => (
    !conta.excluido && (conta.chave === linha.chave || conta.codigo.startsWith(`${ linha.codigo }.`))
      ? { ...conta, tipo }
      : conta
  )))

  const adicionar = pai => {
    const codigo = proximoCodigo(ativas, pai ? pai.codigo : null)
    if (!codigo) return
    contadorNovas.current += 1
    const chave = `novo-${ contadorNovas.current }`
    setContas(atuais => [...atuais, {
      chave,
      codigo,
      descricao: "",
      tipo: pai ? naturezaDe(pai.tipo) : "D",
      dre: pai ? pai.dre || 0 : 0,
      conta_sped: "0",
      lancamentos: 0,
      novo: true,
      excluido: false,
    }])
    if (pai) setRecolhidos(atuais => { const novos = new Set(atuais); novos.delete(pai.codigo); return novos })
    setFocoChave(chave)
  }

  const adicionarIrma = linha => {
    if (!permissoes.inserir) return
    const pai = codigoPai(linha.codigo)
    adicionar(pai ? ativas.find(conta => conta.codigo === pai) : null)
  }

  // Mover troca o código com a irmã vizinha; as excluídas ficam de fora da troca
  const mover = (codigo, direcao) => {
    const movidas = moverConta(ativas, codigo, direcao)
    if (movidas === ativas) return
    const porChave = new Map(movidas.map(conta => [conta.chave, conta]))
    setContas(atuais => atuais.map(conta => porChave.get(conta.chave) || conta))
  }

  const excluir = linha => {
    const codigos = ativas.filter(conta => conta.codigo === linha.codigo || conta.codigo.startsWith(`${ linha.codigo }.`))
    const chaves = new Set(codigos.map(conta => conta.chave))
    setContas(atuais => atuais
      .filter(conta => !(conta.novo && chaves.has(conta.chave)))
      .map(conta => (chaves.has(conta.chave) ? { ...conta, excluido: true } : conta)))
  }

  const alternarRecolher = codigo => setRecolhidos(atuais => {
    const novos = new Set(atuais)
    if (novos.has(codigo)) novos.delete(codigo)
    else novos.add(codigo)
    return novos
  })

  const agrupadoras = useMemo(
    () => ativas.filter(conta => ativas.some(outra => outra.codigo.startsWith(`${ conta.codigo }.`))).map(conta => conta.codigo),
    [ativas]
  )

  // Com alguma conta recolhida, o botão expande tudo; com tudo aberto, recolhe tudo
  const expandirOuRecolherTudo = () => setRecolhidos(recolhidos.size > 0 ? new Set() : new Set(agrupadoras))

  const montarItens = () => {
    const itens = []
    const chaves = []
    for (const conta of contas) {
      const campos = {
        codigo: conta.codigo,
        descricao: conta.descricao,
        tipo: naturezaDe(conta.tipo),
        dre: Number(conta.dre || 0),
        conta_sped: conta.conta_sped || "0",
      }
      if (conta.novo) {
        itens.push(campos)
      } else if (conta.excluido) {
        itens.push({ id: conta.id, excluir: true })
      } else if (foiAlterada(conta)) {
        itens.push({ id: conta.id, ...camposAlterados(conta) })
      } else {
        continue
      }
      chaves.push(conta.chave)
    }
    return { itens, chaves }
  }

  const salvar = confirmado => {
    if (!empresaId || totalPendentes === 0 || props.saving) return
    const { itens, chaves } = montarItens()
    enviados.current = chaves
    props.salvarPlanoContasLote({ idEmpresa: empresaId, itens, confirmado })
  }

  useEffect(() => {
    if (!props.impactos || props.impactos.length === 0) return
    const texto = props.impactos.map(impacto => t("planocontas.impacto.linha", {
      codigo: impacto.codigo,
      lancamentos: impacto.lancamentos.toLocaleString("pt-BR"),
      campos: impacto.campos.map(campo => t(`planocontas.impacto.campo.${ campo }`)).join(", "),
    })).join("\n")
    swal({
      title: t("planocontas.impacto.titulo"),
      text: `${ texto }\n\n${ t("planocontas.impacto.explicacao") }`,
      icon: "warning",
      buttons: [t("planocontas.impacto.cancelar"), t("planocontas.impacto.confirmar")],
      dangerMode: true,
    }).then(confirmou => {
      props.limparImpactosPlanoContas()
      if (confirmou) salvar(true)
    })
  }, [props.impactos])

  const trocarEmpresa = async novaEmpresa => {
    if (!novaEmpresa || novaEmpresa === empresaId) return
    if (totalPendentes > 0) {
      const descartar = await swal({
        title: t("planocontas.empresa.descartar.titulo"),
        text: t("planocontas.empresa.descartar.texto"),
        icon: "warning",
        buttons: [t("planocontas.impacto.cancelar"), t("planocontas.empresa.descartar.confirmar")],
        dangerMode: true,
      })
      if (!descartar) return
    }
    setEmpresaId(novaEmpresa)
    setRecolhidos(new Set())
    props.buscarPlanoContasCadastro(novaEmpresa)
  }

  const contaAvancado = contas.find(conta => conta.chave === avancado)
  const abrirAvancado = chave => {
    setSpedAvancado(contas.find(conta => conta.chave === chave)?.conta_sped || "")
    setAvancado(chave)
  }
  const aplicarAvancado = () => {
    alterar(avancado, { conta_sped: spedAvancado.trim() || "0" })
    setAvancado(null)
  }
  const usuarioDoAvancado = conta => {
    if (conta.novo) return t("planocontas.avancado.naoGravada")
    if (conta.altfunc_Nome) return conta.altfunc_Nome
    return t("planocontas.avancado.sistemaLocal", { codigo: conta.altfunc ?? 0 })
  }

  const aoTeclar = e => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault()
      salvar(false)
    }
  }

  return (
    <div className="page-content" onKeyDown={ aoTeclar }>
      <Container fluid>
        <Breadcrumbs title="menu.label.chart.of.accounts" />
        <StickerToolbarButtons>
          <Button type="button" color="primary" className="waves-effect waves-light me-2"
                  disabled={ totalPendentes === 0 || props.saving } onClick={ () => salvar(false) }>
            { t("planocontas.salvar") }
          </Button>
          <Button type="button" color="secondary" className="waves-effect waves-light me-2"
                  disabled={ totalPendentes === 0 || props.saving } onClick={ () => setContas(paraEdicao(props.contas || [])) }>
            { t("planocontas.descartar") }
          </Button>
          <span role="status" className={ "align-self-center ms-2 " + (totalPendentes ? "text-warning" : "text-muted") }>
            { totalPendentes
              ? t("planocontas.pendentes", { total: totalPendentes, novas: pendentes.novas, alteradas: pendentes.alteradas, excluidas: pendentes.excluidas })
              : t("planocontas.semPendentes") }
          </span>
        </StickerToolbarButtons>
        <Card>
          <CardBody>
            <h4 className="card-title mb-3">{ t("menu.label.chart.of.accounts") }</h4>
            <Form onSubmit={ e => e.preventDefault() }>
              <Row>
                <div className="col-xl col-sm-6">
                  <FormFilterCompany attribute="planoContasEmpresa" value={ empresaId } description={ t("planocontas.filtro.empresa") }
                                     setValue={ trocarEmpresa } options={ empresas } multiple={ false } />
                </div>
                <div className="col-xl col-sm-6">
                  <FormFilterText attribute="planoContasPesquisa" value={ pesquisa } description={ t("planocontas.pesquisa.label") }
                                  changeHandeler={ setPesquisa } />
                </div>
                <div className="col-xl col-sm-6">
                  <FormFilterSelect attribute="planoContasNatureza" value={ filtroNatureza } description={ t("planocontas.filtro.natureza") }
                                    changeHandeler={ setFiltroNatureza }
                                    options={ [
                                      { value: "", label: t("planocontas.filtro.todas", { total: contagem.todas }), defaultValue: true },
                                      { value: "R", label: t("planocontas.filtro.receitas", { total: contagem.R }) },
                                      { value: "D", label: t("planocontas.filtro.despesas", { total: contagem.D }) },
                                    ] } />
                </div>
                <div className="col-xl col-sm-6 align-self-start mt-4 pt-1">
                  <div className="mt-3">
                    <Button color="success" className="w-md" disabled={ !empresaId || !permissoes.inserir || !proximoCodigo(ativas, null) }
                            onClick={ () => adicionar(null) }>
                      { t("planocontas.novaRaiz") }
                    </Button>
                  </div>
                </div>
              </Row>
            </Form>

            <div className="d-flex flex-wrap gap-3 text-muted mt-3 mb-3">
              <span>{ t("planocontas.dica.codigo") }</span>
              <span>{ t("planocontas.dica.agrupa") }</span>
              <span>{ t("planocontas.dica.natureza") }</span>
              <span>{ t("planocontas.dica.mover") }</span>
            </div>

            { props.user && empresas.length === 0 && (
              <div className="alert alert-warning mb-3">{ t("planocontas.semEmpresa") }</div>
            ) }

            { props.loading
              ? <div className="text-center text-muted py-5">{ t("planocontas.carregando") }</div>
              : <PlanoContasGrade linhas={ linhas } grupos={ props.grupos || [] } recolhidos={ recolhidos }
                                  erros={ errosPorChave } permissoes={ permissoes } temAgrupadoras={ agrupadoras.length > 0 }
                                  onAlterar={ alterar } onAlterarNatureza={ alterarNatureza } onAlternarRecolher={ alternarRecolher } onAlternarTodas={ expandirOuRecolherTudo } onMover={ mover }
                                  onAdicionarSubconta={ adicionar } onEnterDescricao={ adicionarIrma }
                                  onAvancado={ abrirAvancado } onExcluir={ excluir } /> }
          </CardBody>
        </Card>

      </Container>

      <Modal isOpen={ Boolean(contaAvancado) } toggle={ () => setAvancado(null) } centered>
        { contaAvancado && (
          <>
            <ModalHeader toggle={ () => setAvancado(null) }>
              { t("planocontas.avancado.titulo") }
              <div className="small text-muted fw-normal">
                <span className="font-monospace">{ contaAvancado.codigo }</span> · { contaAvancado.descricao || t("planocontas.avancado.novaConta") }
              </div>
            </ModalHeader>
            <ModalBody>
              <Label for="plano-contas-sped">{ t("planocontas.avancado.sped") }</Label>
              <Input id="plano-contas-sped" className="font-monospace" maxLength={ 20 } value={ spedAvancado }
                     disabled={ !(contaAvancado.novo ? permissoes.inserir : permissoes.editar) }
                     onChange={ e => setSpedAvancado(e.target.value) } />
              <small className="text-muted">{ t("planocontas.avancado.sped.ajuda") }</small>
              <Row className="mt-3">
                <Col sm={ 6 }>
                  <div className="fw-medium mb-1">{ t("planocontas.avancado.ultimaAlteracao") }</div>
                  <div className="form-control bg-light">
                    { contaAvancado.altdata ? moment(contaAvancado.altdata).format("DD/MM/YYYY HH:mm") : t("planocontas.avancado.semData") }
                  </div>
                </Col>
                <Col sm={ 6 }>
                  <div className="fw-medium mb-1">{ t("planocontas.avancado.usuario") }</div>
                  <div className="form-control bg-light">{ usuarioDoAvancado(contaAvancado) }</div>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={ () => setAvancado(null) }>{ t("planocontas.avancado.cancelar") }</Button>
              <Button color="primary" onClick={ aplicarAvancado }
                      disabled={ !(contaAvancado.novo ? permissoes.inserir : permissoes.editar) }>
                { t("planocontas.avancado.aplicar") }
              </Button>
            </ModalFooter>
          </>
        ) }
      </Modal>
    </div>
  )
}

PlanoContas.propTypes = {
  t: PropTypes.func,
  user: PropTypes.object,
  empresas: PropTypes.array,
  contas: PropTypes.array,
  grupos: PropTypes.array,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  erros: PropTypes.array,
  impactos: PropTypes.array,
  erro: PropTypes.string,
  salvoEm: PropTypes.number,
  location: PropTypes.object,
  buscarPlanoContasCadastro: PropTypes.func,
  salvarPlanoContasLote: PropTypes.func,
  limparImpactosPlanoContas: PropTypes.func,
  navegarBreadCrumb: PropTypes.func,
}

const mapStateToProps = state => {
  const { contas, grupos, loading, saving, erros, impactos, erro, salvoEm } = state.PlanoContas
  return { user: state.Login.user, empresas: state.Profile.empresas, contas, grupos, loading, saving, erros, impactos, erro, salvoEm }
}

export default withTranslation()(connect(mapStateToProps, {
  buscarPlanoContasCadastro,
  salvarPlanoContasLote,
  limparImpactosPlanoContas,
  navegarBreadCrumb,
})(withRouter(PlanoContas)))
